import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/layout/Header';
import { Trophy, Gamepad2, Video, Crown, Zap, Clock, Copy, Monitor, Check, Gift, Key, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { handleTwitchLogin } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format prize display based on prize type
const formatPrizeDisplay = (challenge, colorClass = "text-green-400") => {
  // Check if we have the new prize config structure
  const prize1st = challenge.prize_1st_config;
  const prize2nd = challenge.prize_2nd_config;
  const prize3rd = challenge.prize_3rd_config;
  
  // If we have detailed prize configs, show them
  if (prize1st || prize2nd || prize3rd) {
    const prizes = [];
    
    if (prize1st) {
      if (prize1st.prize_type === 'money') {
        prizes.push({ type: 'money', value: `$${prize1st.amount || 0}`, place: '1st' });
      } else if (prize1st.prize_type === 'game_code') {
        prizes.push({ type: 'game_code', value: prize1st.description || 'Game Code', place: '1st' });
      } else if (prize1st.prize_type === 'item') {
        prizes.push({ type: 'item', value: prize1st.description || 'Prize Item', place: '1st' });
      }
    }
    
    if (prize2nd) {
      if (prize2nd.prize_type === 'money') {
        prizes.push({ type: 'money', value: `$${prize2nd.amount || 0}`, place: '2nd' });
      } else if (prize2nd.prize_type === 'game_code') {
        prizes.push({ type: 'game_code', value: prize2nd.description || 'Game Code', place: '2nd' });
      } else if (prize2nd.prize_type === 'item') {
        prizes.push({ type: 'item', value: prize2nd.description || 'Prize Item', place: '2nd' });
      }
    }
    
    if (prize3rd) {
      if (prize3rd.prize_type === 'money') {
        prizes.push({ type: 'money', value: `$${prize3rd.amount || 0}`, place: '3rd' });
      } else if (prize3rd.prize_type === 'game_code') {
        prizes.push({ type: 'game_code', value: prize3rd.description || 'Game Code', place: '3rd' });
      } else if (prize3rd.prize_type === 'item') {
        prizes.push({ type: 'item', value: prize3rd.description || 'Prize Item', place: '3rd' });
      }
    }
    
    return { hasDetailedPrizes: true, prizes };
  }
  
  // Fallback to simple prize_amount display
  return { 
    hasDetailedPrizes: false, 
    simpleAmount: challenge.prize_amount || 0,
    currency: challenge.prize_currency || 'USD'
  };
};

// Prize Display Component for challenge cards
const PrizeDisplay = ({ challenge, colorClass = "text-green-400", compact = false }) => {
  const prizeInfo = formatPrizeDisplay(challenge, colorClass);
  
  if (prizeInfo.hasDetailedPrizes && prizeInfo.prizes.length > 0) {
    // Show detailed prizes
    if (compact) {
      // Compact view - just show first prize with icon
      const firstPrize = prizeInfo.prizes[0];
      const Icon = firstPrize.type === 'money' ? DollarSign : 
                   firstPrize.type === 'game_code' ? Key : Gift;
      return (
        <div className={`flex items-center gap-1.5 ${colorClass}`}>
          <Icon className="w-4 h-4" />
          <span className="font-bold text-lg truncate max-w-[120px]">
            {firstPrize.value}
          </span>
          {prizeInfo.prizes.length > 1 && (
            <span className="text-xs opacity-70">+{prizeInfo.prizes.length - 1}</span>
          )}
        </div>
      );
    }
    
    // Full view - show all prizes
    return (
      <div className="space-y-1">
        {prizeInfo.prizes.map((prize, idx) => {
          const Icon = prize.type === 'money' ? DollarSign : 
                       prize.type === 'game_code' ? Key : Gift;
          const bgColor = prize.type === 'money' ? 'bg-green-500/10' : 
                         prize.type === 'game_code' ? 'bg-purple-500/10' : 'bg-orange-500/10';
          const textColor = prize.type === 'money' ? 'text-green-400' : 
                           prize.type === 'game_code' ? 'text-purple-400' : 'text-orange-400';
          return (
            <div key={idx} className={`flex items-center gap-2 px-2 py-1 ${bgColor} rounded-lg`}>
              <span className={`text-xs font-bold ${textColor}`}>{prize.place}</span>
              <Icon className={`w-3.5 h-3.5 ${textColor}`} />
              <span className={`text-sm font-medium ${textColor} truncate`}>{prize.value}</span>
            </div>
          );
        })}
      </div>
    );
  }
  
  // Simple money display fallback
  return (
    <span className={`font-bold text-lg ${colorClass}`}>
      ${prizeInfo.simpleAmount}
    </span>
  );
};

// Get Twitch parent domains for embed
const getTwitchParents = () => {
  const hostname = window.location.hostname;
  const parents = [hostname];
  
  // Add common variations
  if (hostname.includes('preview.emergentagent.com')) {
    parents.push('preview.emergentagent.com');
  }
  if (hostname.includes('cesarmchallenges.com') || hostname.includes('cesarmchallenges.dev')) {
    parents.push('cesarmchallenges.com', 'www.cesarmchallenges.com', 'cesarmchallenges.dev', 'www.cesarmchallenges.dev');
  }
  // Always include localhost for development
  if (hostname === 'localhost') {
    parents.push('localhost');
  }
  
  return parents.map(p => `parent=${p}`).join('&');
};

// OBS Link Button Component
const OBSLinkButton = ({ challengeId }) => {
  const [copied, setCopied] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const baseUrl = window.location.origin;
  const obsUrl = `${baseUrl}/obs/challenge/${challengeId}`;
  
  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('OBS Browser Source URL copied!');
    setTimeout(() => setCopied(false), 2000);
    setShowDropdown(false);
  };
  
  const obsOptions = [
    { label: 'Full Overlay (Green)', url: obsUrl },
    { label: 'Neon Theme (Cyan)', url: `${obsUrl}?theme=neon` },
    { label: 'Red Theme', url: `${obsUrl}?theme=red` },
    { label: 'Gold Theme', url: `${obsUrl}?theme=gold` },
    { label: 'Timer Only', url: `${obsUrl}?show=timer` },
    { label: 'Prize Only', url: `${obsUrl}?show=prize` },
    { label: 'Compact Size', url: `${obsUrl}?size=small` },
  ];

  // OBS Studio Logo SVG
  const OBSIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 2.16c5.44 0 9.84 4.4 9.84 9.84 0 5.44-4.4 9.84-9.84 9.84-5.44 0-9.84-4.4-9.84-9.84 0-5.44 4.4-9.84 9.84-9.84zM8.56 6.06c-2.19 0-3.97 1.78-3.97 3.97s1.78 3.97 3.97 3.97c.56 0 1.09-.12 1.57-.33-.19-.45-.3-.94-.3-1.46 0-2.06 1.67-3.73 3.73-3.73.52 0 1.01.11 1.46.3.21-.48.33-1.01.33-1.57 0-2.19-1.78-3.97-3.97-3.97-.56 0-1.09.12-1.57.33.19.45.3.94.3 1.46 0 .21-.02.42-.05.62-.2-.03-.4-.05-.62-.05-.52 0-1.01.11-1.46.3-.21-.48-.33-1.01-.33-1.57 0-.22.02-.43.05-.63-.38.07-.74.18-1.09.33zm6.88 4.15c-2.06 0-3.73 1.67-3.73 3.73s1.67 3.73 3.73 3.73 3.73-1.67 3.73-3.73-1.67-3.73-3.73-3.73z"/>
    </svg>
  );
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
          copied 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 border border-purple-500/20 hover:border-purple-500/40'
        }`}
        title="Get OBS Browser Source URL"
      >
        {copied ? <Check className="w-3 h-3" /> : <OBSIcon className="w-3.5 h-3.5" />}
        <span>OBS Source</span>
      </button>
      
      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
          <div className="absolute bottom-full right-0 mb-1 bg-gray-900/95 backdrop-blur-sm border border-purple-500/30 rounded-lg shadow-xl shadow-purple-500/10 z-50 min-w-[200px] overflow-hidden">
            <div className="px-3 py-2 border-b border-purple-500/20 bg-purple-500/5">
              <p className="text-xs text-purple-300 font-semibold flex items-center gap-1.5">
                <OBSIcon className="w-3.5 h-3.5" />
                OBS Browser Source
              </p>
            </div>
            {obsOptions.map((option, idx) => (
              <button
                key={idx}
                onClick={() => copyToClipboard(option.url)}
                className="w-full px-3 py-2 text-xs text-left text-white hover:bg-gray-800 flex items-center justify-between gap-2"
              >
                <span>{option.label}</span>
                <Copy className="w-3 h-3 text-gray-500" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Helper to get embed URL
const getEmbedUrl = (url, type) => {
  if (type === 'youtube') {
    let videoId = '';
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1]?.split('&')[0];
    } else if (url.includes('/embed/')) {
      videoId = url.split('/embed/')[1]?.split('?')[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  } else if (type === 'twitch') {
    let clipSlug = '';
    if (url.includes('clips.twitch.tv/')) {
      clipSlug = url.split('clips.twitch.tv/')[1]?.split('?')[0];
    } else if (url.includes('/clip/')) {
      clipSlug = url.split('/clip/')[1]?.split('?')[0];
    }
    if (clipSlug) {
      return `https://clips.twitch.tv/embed?clip=${clipSlug}&${getTwitchParents()}`;
    }
  }
  return null;
};

// TimeBlock Component (extracted to fix linting error)
const TimeBlock = ({ value, unit, colorClass, compact = false }) => (
  <div className="flex flex-col items-center">
    <div 
      className={`font-black ${colorClass} ${compact ? 'text-lg sm:text-xl' : 'text-xl sm:text-3xl lg:text-5xl'}`} 
      style={{ fontFamily: 'Orbitron, sans-serif' }}
    >
      {String(value).padStart(2, '0')}
    </div>
    <div className={`uppercase tracking-wider text-gray-500 ${compact ? 'text-[8px]' : 'text-[10px] sm:text-xs'}`}>{unit}</div>
  </div>
);

// Challenge Countdown - for active challenges showing time until end
const ChallengeCountdown = ({ endTime }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!endTime) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(endTime).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setIsExpired(false);
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  if (!endTime) return null;

  if (isExpired) {
    return (
      <div className="mt-2 p-2 bg-red-500/10 rounded-lg border border-red-500/20 text-center">
        <span className="text-red-400 text-xs font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          CHALLENGE ENDED
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 px-2 py-1.5 bg-red-500/5 rounded-lg border border-red-500/15">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[8px] text-red-400 uppercase tracking-wider flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          Time Remaining
        </span>
      </div>
      <div className="flex items-center justify-center gap-1.5">
        {timeLeft.days > 0 && (
          <>
            <div className="text-center">
              <span className="text-sm font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {String(timeLeft.days).padStart(2, '0')}
              </span>
              <span className="text-[7px] text-gray-500 block">DAYS</span>
            </div>
            <span className="text-red-400 font-bold text-xs">:</span>
          </>
        )}
        <div className="text-center">
          <span className="text-sm font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            {String(timeLeft.hours).padStart(2, '0')}
          </span>
          <span className="text-[7px] text-gray-500 block">HRS</span>
        </div>
        <span className="text-red-400 font-bold text-xs">:</span>
        <div className="text-center">
          <span className="text-sm font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </span>
          <span className="text-[7px] text-gray-500 block">MIN</span>
        </div>
        <span className="text-red-400 font-bold text-xs">:</span>
        <div className="text-center">
          <span className="text-sm font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </span>
          <span className="text-[7px] text-gray-500 block">SEC</span>
        </div>
      </div>
    </div>
  );
};

// Compact Countdown for scheduled challenges
const CompactCountdown = ({ targetDate, colorClass = "text-yellow-400" }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setIsExpired(false);
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return null;

  if (isExpired) {
    return (
      <div className={`text-center text-sm font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
        STARTING NOW!
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <div className="flex flex-col items-center min-w-[28px]">
        <span className={`text-sm font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {String(timeLeft.days).padStart(2, '0')}
        </span>
        <span className="text-[7px] text-gray-500 uppercase">Days</span>
      </div>
      <span className={`text-sm font-bold ${colorClass} -mt-2`}>:</span>
      <div className="flex flex-col items-center min-w-[28px]">
        <span className={`text-sm font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {String(timeLeft.hours).padStart(2, '0')}
        </span>
        <span className="text-[7px] text-gray-500 uppercase">Hrs</span>
      </div>
      <span className={`text-sm font-bold ${colorClass} -mt-2`}>:</span>
      <div className="flex flex-col items-center min-w-[28px]">
        <span className={`text-sm font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {String(timeLeft.minutes).padStart(2, '0')}
        </span>
        <span className="text-[7px] text-gray-500 uppercase">Min</span>
      </div>
      <span className={`text-sm font-bold ${colorClass} -mt-2`}>:</span>
      <div className="flex flex-col items-center min-w-[28px]">
        <span className={`text-sm font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
        <span className="text-[7px] text-gray-500 uppercase">Sec</span>
      </div>
    </div>
  );
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate, label, colorClass }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setIsExpired(false);
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return null;

  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Clock className={`w-5 h-5 ${colorClass}`} />
        <span className={`text-sm uppercase tracking-widest ${colorClass}`}>{label}</span>
      </div>
      {isExpired ? (
        <div className={`text-2xl font-bold ${colorClass}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {label.includes('Start') ? 'STARTED!' : 'ENDED!'}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3 sm:gap-6">
          <TimeBlock value={timeLeft.days} unit="Days" colorClass={colorClass} />
          <span className={`text-2xl sm:text-3xl font-bold ${colorClass}`}>:</span>
          <TimeBlock value={timeLeft.hours} unit="Hours" colorClass={colorClass} />
          <span className={`text-2xl sm:text-3xl font-bold ${colorClass}`}>:</span>
          <TimeBlock value={timeLeft.minutes} unit="Mins" colorClass={colorClass} />
          <span className={`text-2xl sm:text-3xl font-bold ${colorClass}`}>:</span>
          <TimeBlock value={timeLeft.seconds} unit="Secs" colorClass={colorClass} />
        </div>
      )}
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [settings, setSettings] = useState({ 
    prize_amount: 100, 
    prize_currency: 'USD', 
    contest_active: true,
    challenge_title: 'Submit Your Best Video',
    challenge_rules: '',
    challenge_start_time: null,
    challenge_end_time: null,
    game_id: null,
    game_name: null,
    game_cover_url: null
  });
  const [latestWinner, setLatestWinner] = useState(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [liveStreams, setLiveStreams] = useState([]);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [scheduledChallenges, setScheduledChallenges] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, winnerRes, submissionsRes, streamsRes, activeRes, scheduledRes, statsRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/winners/latest`),
          axios.get(`${API}/submissions`),
          axios.get(`${API}/live-streams?limit=10`),
          axios.get(`${API}/challenges/active`),
          axios.get(`${API}/challenges/scheduled`),
          axios.get(`${API}/stats`)
        ]);
        setSettings(settingsRes.data && typeof settingsRes.data === 'object' ? settingsRes.data : {});
        setLatestWinner(winnerRes.data && typeof winnerRes.data === 'object' ? winnerRes.data : null);
        setSubmissionCount(Array.isArray(submissionsRes.data) ? submissionsRes.data.length : 0);
        setLiveStreams(Array.isArray(streamsRes.data) ? streamsRes.data : []);
        setActiveChallenges(Array.isArray(activeRes.data) ? activeRes.data : []);
        setScheduledChallenges(Array.isArray(scheduledRes.data) ? scheduledRes.data : []);
        setTotalUsers(statsRes.data && typeof statsRes.data === 'object' ? (statsRes.data.total_users || 0) : 0);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
    
    // Refresh data every 30 seconds to catch timer changes
    const interval = setInterval(async () => {
      try {
        const [streamsRes, activeRes, scheduledRes] = await Promise.all([
          axios.get(`${API}/live-streams?limit=10`),
          axios.get(`${API}/challenges/active`),
          axios.get(`${API}/challenges/scheduled`)
        ]);
        setLiveStreams(Array.isArray(streamsRes.data) ? streamsRes.data : []);
        setActiveChallenges(Array.isArray(activeRes.data) ? activeRes.data : []);
        setScheduledChallenges(Array.isArray(scheduledRes.data) ? scheduledRes.data : []);
      } catch (e) {
        // Silent fail for background refresh
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleLogin = handleTwitchLogin;

  // Safe array references
  const safeActiveChallenges = Array.isArray(activeChallenges) ? activeChallenges : [];
  const safeScheduledChallenges = Array.isArray(scheduledChallenges) ? scheduledChallenges : [];

  // Check if challenge has started - use active challenges if available
  const hasActiveChallenges = safeActiveChallenges.length > 0;
  const hasStarted = hasActiveChallenges || (settings.challenge_start_time && new Date(settings.challenge_start_time) <= new Date());
  const hasEnded = !hasActiveChallenges && settings.challenge_end_time && new Date(settings.challenge_end_time) <= new Date();
  const isLive = hasActiveChallenges || (hasStarted && !hasEnded);
  
  // Helper to calculate total money from prize configs
  const calculatePrizePoolFromConfigs = (challenge) => {
    if (!challenge) return 0;
    let total = 0;
    const configs = [challenge.prize_1st_config, challenge.prize_2nd_config, challenge.prize_3rd_config];
    configs.forEach(config => {
      if (config && config.prize_type === 'money' && config.amount) {
        total += parseFloat(config.amount) || 0;
      }
    });
    // If no configs, use legacy prize_amount
    if (total === 0) {
      total = parseFloat(challenge.prize_amount) || 0;
    }
    return total;
  };
  
  // Calculate total prize pool from active challenges (only money prizes)
  const totalPrizePool = hasActiveChallenges 
    ? safeActiveChallenges.reduce((sum, c) => sum + calculatePrizePoolFromConfigs(c), 0)
    : 0;
  
  // Calculate upcoming prize pool (only money prizes)
  const upcomingPrizePool = safeScheduledChallenges.reduce((sum, c) => sum + calculatePrizePoolFromConfigs(c), 0);
  
  // Count non-money prizes for display
  const countNonMoneyPrizes = (challenges) => {
    let gameCodes = 0;
    let items = 0;
    (Array.isArray(challenges) ? challenges : []).forEach(c => {
      const configs = [c.prize_1st_config, c.prize_2nd_config, c.prize_3rd_config];
      configs.forEach(config => {
        if (config) {
          if (config.prize_type === 'game_code') gameCodes++;
          if (config.prize_type === 'item') items++;
        }
      });
    });
    return { gameCodes, items };
  };
  
  const activeNonMoney = countNonMoneyPrizes(activeChallenges);
  const scheduledNonMoney = countNonMoneyPrizes(scheduledChallenges);
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <Header />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20 pb-8"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9)), url('https://customer-assets.emergentagent.com/job_twitchwin/artifacts/1celejxf_oberin-ojm-escape-from-tarkov-wallpaper-3840x2160-d3hell.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="text-center px-4 max-w-4xl mx-auto">
          {/* Animated Title */}
          <div className="relative mb-6 sm:mb-8">
            {/* Glow effect behind text */}
            <div 
              className="absolute inset-0 blur-3xl opacity-60"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(255,0,0,0.4) 0%, transparent 70%)'
              }}
            />
            
            <h1 
              className="relative text-4xl sm:text-6xl lg:text-8xl font-black tracking-tight"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
              data-testid="hero-title"
            >
              {/* CesarM with gradient animation */}
              <span 
                className="inline-block animate-pulse-slow"
                style={{
                  background: 'linear-gradient(90deg, #ff0000, #ff4444, #ff0000, #cc0000)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'gradient-shift 3s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 30px rgba(255, 0, 0, 0.5))'
                }}
              >
                CESARM
              </span>
              <br />
              {/* CHALLENGES with separate animation */}
              <span 
                className="inline-block text-3xl sm:text-5xl lg:text-6xl mt-2"
                style={{
                  background: 'linear-gradient(90deg, #ffffff, #ff6666, #ffffff)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'gradient-shift 3s ease-in-out infinite reverse',
                  letterSpacing: '0.3em',
                  filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))'
                }}
              >
                CHALLENGES
              </span>
            </h1>
            
            {/* Animated underline */}
            <div 
              className="h-1 mx-auto mt-4 rounded-full"
              style={{
                width: '60%',
                background: 'linear-gradient(90deg, transparent, #ff0000, #ff4444, #ff0000, transparent)',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Keyframes for animations - injected via style tag */}
          <style>{`
            @keyframes gradient-shift {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            @keyframes pulse-glow {
              0%, 100% { filter: drop-shadow(0 0 30px rgba(255, 0, 0, 0.5)); }
              50% { filter: drop-shadow(0 0 50px rgba(255, 0, 0, 0.8)); }
            }
          `}</style>
          
          {/* Status Badge under title */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-4">
            <span className={`px-3 py-1 rounded text-sm font-bold uppercase flex items-center gap-2 ${
              isLive ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 
              scheduledChallenges.length > 0 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 
              hasEnded ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 
              'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
            }`}>
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : scheduledChallenges.length > 0 ? 'bg-yellow-400' : hasEnded ? 'bg-gray-400' : 'bg-yellow-400'}`}></span>
              {isLive ? 'LIVE' : scheduledChallenges.length > 0 ? 'COMING SOON' : hasEnded ? 'ENDED' : 'COMING SOON'}
            </span>
            <span className="px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-sm font-bold flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              ${Math.round(totalPrizePool)} Cash
              {hasActiveChallenges && activeChallenges.length > 1 && (
                <span className="text-xs ml-1 text-gray-500">({activeChallenges.length} challenges)</span>
              )}
            </span>
            {/* Show game codes and items count */}
            {(activeNonMoney.gameCodes > 0 || activeNonMoney.items > 0) && (
              <span className="px-3 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-sm font-bold flex items-center gap-2">
                {activeNonMoney.gameCodes > 0 && (
                  <span className="flex items-center gap-1">
                    <Key className="w-3 h-3" />
                    {activeNonMoney.gameCodes}
                  </span>
                )}
                {activeNonMoney.items > 0 && (
                  <span className="flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    {activeNonMoney.items}
                  </span>
                )}
              </span>
            )}
            {scheduledChallenges.length > 0 && (
              <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded text-sm font-bold">
                +${Math.round(upcomingPrizePool)} upcoming
                {(scheduledNonMoney.gameCodes > 0 || scheduledNonMoney.items > 0) && (
                  <span className="flex items-center gap-1 text-xs">
                    {scheduledNonMoney.gameCodes > 0 && <><Key className="w-3 h-3" /> {scheduledNonMoney.gameCodes}</>}
                    {scheduledNonMoney.items > 0 && <><Gift className="w-3 h-3" /> {scheduledNonMoney.items}</>}
                  </span>
                )}
              </span>
            )}
          </div>
          
          {/* Active Challenges Display - Show ALL */}
          {activeChallenges.length > 0 && (
            <div className="mb-6 sm:mb-8" data-testid="active-challenges">
              <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4 text-center">
                {activeChallenges.length} Active Challenge{activeChallenges.length > 1 ? 's' : ''}
              </h3>
              <div className={`grid gap-4 ${activeChallenges.length === 1 ? 'max-w-xl mx-auto' : 'md:grid-cols-2 max-w-4xl mx-auto'}`}>
                {activeChallenges.map((challenge) => (
                  <Card key={challenge.id} className="glass-card border-green-500/20 overflow-hidden">
                    {/* Header Bar */}
                    <div className="bg-gradient-to-r from-green-500/10 to-transparent px-4 py-2 flex items-center justify-between border-b border-green-500/10">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        <span className="text-green-400 font-bold text-xs uppercase tracking-wider">Live</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <PrizeDisplay challenge={challenge} colorClass="text-green-400" compact={true} />
                        {challenge.game_name && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded-full">
                            <Gamepad2 className="w-3 h-3" />
                            {challenge.game_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex gap-4">
                        {/* Game Cover */}
                        {challenge.game_cover_url && (
                          <img 
                            src={challenge.game_cover_url} 
                            alt={challenge.game_name}
                            className="w-20 h-28 object-cover rounded-lg shadow-lg shadow-green-500/10 flex-shrink-0"
                          />
                        )}
                        
                        {/* Challenge Info */}
                        <div className="flex-1 min-w-0">
                          {/* Challenge Title */}
                          <h3 
                            className="text-lg font-bold text-white mb-3"
                            style={{ fontFamily: 'Orbitron, sans-serif' }}
                          >
                            {challenge.title}
                          </h3>
                          
                          {/* Countdown Timer */}
                          {challenge.end_time && (
                            <ChallengeCountdown endTime={challenge.end_time} />
                          )}
                        </div>
                      </div>
                      
                      {/* Rules Section */}
                      {(challenge.rules || challenge.rules_config?.length > 0) && (
                        <div className="mt-4 p-3 bg-black/20 rounded-lg border border-gray-800/50 text-left overflow-hidden">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                            Rules
                          </p>
                          <ul className="space-y-1 text-left">
                            {/* Use rules_config if available, otherwise fall back to rules string */}
                            {challenge.rules_config && challenge.rules_config.length > 0 ? (
                              challenge.rules_config
                                .filter(r => r.visibility === 'always' || r.visibility === 'live_only')
                                .map((rule, i) => (
                                  <li key={i} className="text-xs text-gray-300 flex items-start gap-2 text-left">
                                    <span className="text-green-400 mt-0.5 flex-shrink-0">•</span>
                                    <span className="text-left break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rule.text}</span>
                                  </li>
                                ))
                            ) : (
                              challenge.rules.split('\n').filter(r => r.trim()).map((rule, i) => (
                                <li key={i} className="text-xs text-gray-300 flex items-start gap-2 text-left">
                                  <span className="text-green-400 mt-0.5 flex-shrink-0">•</span>
                                  <span className="text-left break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rule.trim()}</span>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {/* Prizes Section - Show detailed prizes if available */}
                      {(challenge.prize_1st_config || challenge.prize_2nd_config || challenge.prize_3rd_config) && (
                        <div className="mt-4 p-3 bg-black/20 rounded-lg border border-green-500/20">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Trophy className="w-3 h-3 text-green-400" />
                            Prizes
                          </p>
                          <PrizeDisplay challenge={challenge} colorClass="text-green-400" compact={false} />
                        </div>
                      )}
                      
                      {/* Bounties Section */}
                      {challenge.bounties && challenge.bounties.length > 0 && (
                        <div className="mt-4 p-3 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/30">
                          <p className="text-[10px] text-yellow-400 uppercase tracking-wider mb-2 font-bold flex items-center gap-1">
                            <Gift className="w-3 h-3" />
                            Bonus Bounties
                          </p>
                          <div className="space-y-2">
                            {challenge.bounties.map((bounty, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-left">
                                <span className="text-yellow-400 text-sm mt-0.5">⭐</span>
                                <div className="flex-1">
                                  <p className="text-xs text-yellow-300 font-semibold">{bounty.prize}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">└─ {bounty.rule}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Footer Stats */}
                      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          {challenge.submission_count || 0} submissions
                        </span>
                        <div className="flex items-center gap-2">
                          <OBSLinkButton challengeId={challenge.id} />
                          <span className="text-xs text-gray-500">
                            {challenge.max_winners || 1} winner{(challenge.max_winners || 1) > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Challenges - Coming Soon */}
          {scheduledChallenges.length > 0 && (
            <div className="mb-6 sm:mb-8" data-testid="scheduled-challenges">
              <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4 text-center">
                {scheduledChallenges.length} Upcoming Challenge{scheduledChallenges.length > 1 ? 's' : ''}
              </h3>
              <div className={`grid gap-4 ${scheduledChallenges.length === 1 ? 'max-w-xl mx-auto' : 'md:grid-cols-2 max-w-4xl mx-auto'}`}>
                {scheduledChallenges.map((challenge) => (
                  <Card key={challenge.id} className="glass-card border-yellow-500/20 overflow-hidden">
                    {/* Header Bar */}
                    <div className="bg-gradient-to-r from-yellow-500/10 to-transparent px-4 py-2 flex items-center justify-between border-b border-yellow-500/10">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                        <span className="text-yellow-400 font-bold text-xs uppercase tracking-wider">Scheduled</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <PrizeDisplay challenge={challenge} colorClass="text-yellow-400" compact={true} />
                        {challenge.game_name && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded-full">
                            <Gamepad2 className="w-3 h-3" />
                            {challenge.game_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex gap-4">
                        {/* Game Cover */}
                        {challenge.game_cover_url && (
                          <img 
                            src={challenge.game_cover_url} 
                            alt={challenge.game_name}
                            className="w-20 h-28 object-cover rounded-lg shadow-lg shadow-yellow-500/10 flex-shrink-0"
                          />
                        )}
                        
                        {/* Challenge Info */}
                        <div className="flex-1 min-w-0">
                          {/* Challenge Title */}
                          <h3 
                            className="text-lg font-bold text-white mb-3"
                            style={{ fontFamily: 'Orbitron, sans-serif' }}
                          >
                            {challenge.title}
                          </h3>
                          
                          {/* Countdown Timer */}
                          {challenge.start_time && (
                            <div className="px-2 py-1.5 bg-yellow-500/5 rounded-lg border border-yellow-500/15">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[8px] text-yellow-400 uppercase tracking-wider flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5" />
                                  Starts In
                                </span>
                              </div>
                              <CompactCountdown 
                                targetDate={challenge.start_time}
                                colorClass="text-yellow-400"
                              />
                            </div>
                          )}
                          
                          {/* No start time set */}
                          {!challenge.start_time && (
                            <div className="bg-gray-500/5 border border-gray-500/15 rounded-lg px-2 py-1.5 text-center">
                              <p className="text-xs text-gray-400">Start time not set yet</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Rules Section - Based on visibility settings for upcoming */}
                      {(challenge.rules || challenge.rules_config?.length > 0) && (
                        <div className="mt-4 p-3 bg-black/20 rounded-lg border border-gray-800/50 relative overflow-hidden text-left">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <span className="w-1 h-1 bg-yellow-500 rounded-full"></span>
                            Rules
                          </p>
                          
                          {/* Rules with visibility controls */}
                          {challenge.rules_config && challenge.rules_config.length > 0 ? (
                            <ul className="space-y-1 text-left">
                              {challenge.rules_config.map((rule, i) => {
                                // Always visible rules
                                if (rule.visibility === 'always') {
                                  return (
                                    <li key={i} className="text-xs text-gray-300 flex items-start gap-2 text-left">
                                      <span className="text-green-400 mt-0.5 flex-shrink-0">•</span>
                                      <span className="text-left break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rule.text}</span>
                                    </li>
                                  );
                                }
                                // Blurred rules
                                if (rule.visibility === 'blur_scheduled') {
                                  return (
                                    <li key={i} className="text-xs text-gray-300 flex items-start gap-2 blur-sm select-none text-left">
                                      <span className="text-yellow-400 mt-0.5 flex-shrink-0">•</span>
                                      <span className="text-left break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rule.text}</span>
                                    </li>
                                  );
                                }
                                // Live only rules - hidden completely
                                return null;
                              })}
                            </ul>
                          ) : (
                            /* Legacy rules - all blurred for scheduled */
                            <>
                              <div className="blur-sm select-none">
                                <ul className="space-y-1 text-left">
                                  {challenge.rules.split('\n').filter(r => r.trim()).map((rule, i) => (
                                    <li key={i} className="text-xs text-gray-300 flex items-start gap-2 text-left">
                                      <span className="text-yellow-400 mt-0.5 flex-shrink-0">•</span>
                                      <span className="text-left break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rule.trim()}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <span className="text-xs text-yellow-400/80 font-medium px-2 py-1 bg-black/50 rounded">
                                  Rules revealed when challenge starts
                                </span>
                              </div>
                            </>
                          )}
                          
                          {/* Show hint if there are hidden rules */}
                          {challenge.rules_config && challenge.rules_config.some(r => r.visibility === 'live_only' || r.visibility === 'blur_scheduled') && (
                            <p className="text-[10px] text-yellow-400/60 mt-2 italic text-left">
                              🔒 Some rules will be revealed when challenge goes live
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Prizes Section - Show detailed prizes if available */}
                      {(challenge.prize_1st_config || challenge.prize_2nd_config || challenge.prize_3rd_config) && (
                        <div className="mt-4 p-3 bg-black/20 rounded-lg border border-yellow-500/20">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Trophy className="w-3 h-3 text-yellow-400" />
                            Prizes
                          </p>
                          <PrizeDisplay challenge={challenge} colorClass="text-yellow-400" compact={false} />
                        </div>
                      )}
                      
                      {/* Bounties Section */}
                      {challenge.bounties && challenge.bounties.length > 0 && (
                        <div className="mt-4 p-3 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/30">
                          <p className="text-[10px] text-yellow-400 uppercase tracking-wider mb-2 font-bold flex items-center gap-1">
                            <Gift className="w-3 h-3" />
                            Bonus Bounties
                          </p>
                          <div className="space-y-2">
                            {challenge.bounties.map((bounty, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-left">
                                <span className="text-yellow-400 text-sm mt-0.5">⭐</span>
                                <div className="flex-1">
                                  <p className="text-xs text-yellow-300 font-semibold">{bounty.prize}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">└─ {bounty.rule}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Footer Stats */}
                      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Opens soon
                        </span>
                        <div className="flex items-center gap-2">
                          <OBSLinkButton challengeId={challenge.id} />
                          <span className="text-xs text-gray-500">
                            {challenge.max_winners || 1} winner{(challenge.max_winners || 1) > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Featured Game Display (fallback when no active challenges) */}
          {activeChallenges.length === 0 && settings.game_name && (
            <div className="mb-6 sm:mb-8" data-testid="featured-game">
              <Card className="glass-card border-purple-500/30 p-4 inline-block">
                <div className="flex items-center gap-4">
                  {settings.game_cover_url && (
                    <img 
                      src={settings.game_cover_url} 
                      alt={settings.game_name}
                      className="w-16 h-20 sm:w-20 sm:h-28 object-cover rounded-lg shadow-lg shadow-purple-500/20"
                    />
                  )}
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                      <span className="text-xs sm:text-sm text-purple-400 uppercase tracking-wider font-semibold">Featured Game</span>
                    </div>
                    <h3 
                      className="text-lg sm:text-2xl font-bold text-white"
                      style={{ fontFamily: 'Orbitron, sans-serif' }}
                    >
                      {settings.game_name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 mt-1">
                      Submit your best videos from this game!
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Legacy Countdown Timers - Only show when no active challenges are using their own timers */}
          {!hasActiveChallenges && (
            <div className="mb-6 sm:mb-8 space-y-4 sm:space-y-6">
              {!hasStarted && settings.challenge_start_time && (
                <Card className="glass-card p-4 sm:p-6 border-yellow-500/30">
                  <CountdownTimer 
                    targetDate={settings.challenge_start_time} 
                    label="Challenge Starts In"
                    colorClass="text-yellow-400"
                  />
                </Card>
              )}
              
              {hasStarted && settings.challenge_end_time && !hasEnded && (
                <Card className="glass-card p-6 border-red-500/30">
                  <CountdownTimer 
                    targetDate={settings.challenge_end_time} 
                    label="Challenge Ends In"
                    colorClass="text-red-400"
                  />
                </Card>
              )}
            </div>
          )}
          
          {/* Prize Display */}
          <div className="mb-10">
            <p className="text-gray-500 uppercase tracking-widest text-sm mb-2">Current Prize Pool</p>
            <div className="flex flex-col items-center gap-3">
              {/* Cash prizes */}
              {totalPrizePool > 0 && (
                <div className="text-6xl sm:text-7xl lg:text-8xl font-black prize-glow gradient-text" data-testid="prize-display">
                  ${Math.round(totalPrizePool).toLocaleString()}
                </div>
              )}
              {/* Non-money prizes display */}
              {(activeNonMoney.gameCodes > 0 || activeNonMoney.items > 0 || scheduledNonMoney.gameCodes > 0 || scheduledNonMoney.items > 0) && (
                <div className="flex items-center gap-4 flex-wrap justify-center">
                  {(activeNonMoney.gameCodes + scheduledNonMoney.gameCodes) > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg">
                      <Key className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-400 font-bold text-lg">
                        {activeNonMoney.gameCodes + scheduledNonMoney.gameCodes} Game Code{(activeNonMoney.gameCodes + scheduledNonMoney.gameCodes) > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  {(activeNonMoney.items + scheduledNonMoney.items) > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                      <Gift className="w-5 h-5 text-orange-400" />
                      <span className="text-orange-400 font-bold text-lg">
                        {activeNonMoney.items + scheduledNonMoney.items} Prize Item{(activeNonMoney.items + scheduledNonMoney.items) > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {/* No prizes fallback */}
              {totalPrizePool === 0 && activeNonMoney.gameCodes === 0 && activeNonMoney.items === 0 && scheduledNonMoney.gameCodes === 0 && scheduledNonMoney.items === 0 && (
                <div className="text-4xl sm:text-5xl font-bold text-gray-500">
                  Prizes Coming Soon
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          {isAuthenticated ? (
            <Button 
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-lg px-10 py-6 skew-btn neon-glow"
              onClick={() => navigate('/submit')}
              data-testid="hero-submit-btn"
              disabled={!hasStarted || hasEnded}
            >
              <span className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                {!hasStarted ? 'Coming Soon' : hasEnded ? 'Challenge Ended' : 'Submit Your Video'}
              </span>
            </Button>
          ) : (
            <Button 
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-lg px-10 py-6 skew-btn neon-glow"
              onClick={handleLogin}
              data-testid="hero-login-btn"
            >
              <span className="flex items-center gap-2">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
                Login with Twitch to Enter
              </span>
            </Button>
          )}
        </div>
      </section>

      {/* Live EFT Streams Section */}
      {liveStreams.length > 0 && (
        <section className="py-12 sm:py-16 px-3 sm:px-4 border-t border-red-900/20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-3xl font-bold text-red-500 mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                  Live #CesarMChallenges Streams
                </span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base">Watch registered community members streaming with #CesarMChallenges</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {liveStreams.map((stream, index) => (
                <Card 
                  key={stream.twitch_username} 
                  className="glass-card overflow-hidden group hover:-translate-y-1 transition-all duration-300 hover:border-purple-500/50 border-red-900/30"
                >
                  {/* Embedded Twitch Stream */}
                  <div className="relative aspect-video bg-black">
                    <iframe
                      src={`https://player.twitch.tv/?channel=${stream.twitch_username}&${getTwitchParents()}&muted=true&autoplay=false`}
                      height="100%"
                      width="100%"
                      allowFullScreen
                      className="absolute inset-0"
                      title={`${stream.display_name}'s stream`}
                    />
                    {/* Live badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 rounded text-xs font-bold flex items-center gap-1">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      LIVE
                    </div>
                    {/* Viewer count */}
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/70 rounded text-xs flex items-center gap-1">
                      <svg className="w-3 h-3 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                      {stream.viewer_count.toLocaleString()}
                    </div>
                  </div>
                  
                  {/* Stream Info */}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      {stream.profile_image_url ? (
                        <img 
                          src={stream.profile_image_url} 
                          alt={stream.display_name}
                          className="w-8 h-8 rounded-full border border-purple-500"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-purple-400 truncate">{stream.display_name}</p>
                        {stream.is_registered && (
                          <span className="text-[10px] text-green-400">✓ Registered</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2">{stream.title}</p>
                    <a 
                      href={`https://twitch.tv/${stream.twitch_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
                    >
                      Watch on Twitch
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Winner Section - With Video - MOVED ABOVE HOW IT WORKS */}
      {latestWinner && (
        <section className="py-12 sm:py-24 px-3 sm:px-4 border-b border-red-900/20 bg-gradient-to-b from-black to-red-950/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 text-yellow-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <Crown className="inline-block w-8 h-8 mr-2 animate-bounce" />
              Latest Winner
            </h2>
            
            <Card className="glass-card overflow-hidden border-yellow-500/30 hover:border-yellow-400/50 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-6 p-6">
                {/* Winner Info */}
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative">
                      <img 
                        src={latestWinner.profile_image_url || 'https://via.placeholder.com/80'} 
                        alt={latestWinner.display_name}
                        className="w-20 h-20 rounded-full border-4 border-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.4)]"
                      />
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center animate-pulse">
                        <Crown className="w-5 h-5 text-black" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-yellow-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {latestWinner.display_name}
                      </h3>
                      <p className="text-gray-400">@{latestWinner.twitch_username}</p>
                    </div>
                  </div>
                  
                  {/* Challenge Title & Game Name Badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {latestWinner.challenge_title && (
                      <span className="px-3 py-1.5 bg-blue-500/20 text-blue-400 text-sm rounded-full font-medium border border-blue-500/30 flex items-center gap-1.5">
                        <Trophy className="w-4 h-4" />
                        {latestWinner.challenge_title}
                      </span>
                    )}
                    {latestWinner.game_name && (
                      <span className="px-3 py-1.5 bg-purple-500/20 text-purple-400 text-sm rounded-full font-medium border border-purple-500/30 flex items-center gap-1.5">
                        <Gamepad2 className="w-4 h-4" />
                        {latestWinner.game_name}
                      </span>
                    )}
                    {/* Place Badge */}
                    <span className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 ${
                      latestWinner.place === 1 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      latestWinner.place === 2 ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' :
                      latestWinner.place === 3 ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30' :
                      'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {latestWinner.place === 1 || !latestWinner.place ? '🥇 1st Place' : latestWinner.place === 2 ? '🥈 2nd Place' : '🥉 3rd Place'}
                    </span>
                  </div>
                  
                  {/* Game Cover Image */}
                  {latestWinner.game_cover_url && (
                    <div className="flex items-center gap-3 mb-4 p-2 bg-black/30 rounded-lg border border-purple-500/20 w-fit">
                      <img 
                        src={latestWinner.game_cover_url} 
                        alt={latestWinner.game_name}
                        className="w-12 h-16 object-cover rounded"
                      />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Won Playing</p>
                        <p className="text-purple-400 font-medium">{latestWinner.game_name}</p>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-4xl font-black text-green-400 mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    Won ${latestWinner.prize_amount} {latestWinner.prize_currency !== 'USD' && latestWinner.prize_currency}
                  </p>

                  <Button 
                    variant="outline"
                    className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 w-fit"
                    onClick={() => navigate('/winners')}
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    View All Winners
                  </Button>
                </div>

                {/* Video Embed */}
                <div className="aspect-video bg-black/50 rounded-lg overflow-hidden border border-yellow-500/20">
                  {(() => {
                    const isYoutube = latestWinner.video_url?.includes('youtube') || latestWinner.video_url?.includes('youtu.be');
                    const embedUrl = getEmbedUrl(latestWinner.video_url, isYoutube ? 'youtube' : 'twitch');
                    
                    if (embedUrl) {
                      return (
                        <iframe
                          src={embedUrl}
                          title={`${latestWinner.display_name}'s winning video`}
                          className="w-full h-full"
                          frameBorder="0"
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      );
                    }
                    return (
                      <a 
                        href={latestWinner.video_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-full h-full flex items-center justify-center hover:bg-white/5 transition-colors"
                      >
                        <div className="text-center">
                          <Video className="w-16 h-16 text-red-500 mx-auto mb-2" />
                          <span className="text-gray-400">Click to watch winning video</span>
                        </div>
                      </a>
                    );
                  })()}
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-12 sm:py-24 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8 sm:mb-16 text-red-500" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            How It Works
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            <Card className="glass-card p-5 sm:p-8 text-center group hover:-translate-y-2 transition-all duration-300 hover:border-red-500/50 border-red-900/30">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all">
                <Gamepad2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 uppercase text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>1. Login</h3>
              <p className="text-gray-400">Connect with your Twitch account to participate in the contest</p>
            </Card>

            <Card className="glass-card p-5 sm:p-8 text-center group hover:-translate-y-2 transition-all duration-300 hover:border-red-500/50 border-red-900/30">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all">
                <Video className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 uppercase text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>2. Submit</h3>
              <p className="text-gray-400">Share your best Twitch video or YouTube video link</p>
            </Card>

            <Card className="glass-card p-5 sm:p-8 text-center group hover:-translate-y-2 transition-all duration-300 hover:border-red-500/50 border-red-900/30">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all">
                <Crown className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 uppercase text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>3. Win</h3>
              <p className="text-gray-400">Get selected by the admin and claim your prize!</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-16 px-3 sm:px-4 border-y border-red-900/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {/* Challenges */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-transparent rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-50"></div>
              <div className="relative bg-black/40 backdrop-blur-sm border border-red-500/30 rounded-xl p-6 text-center hover:border-red-500/50 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-red-400" />
                </div>
                <div className="text-4xl font-black text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {activeChallenges.length + scheduledChallenges.length}
                </div>
                <p className="text-red-400 uppercase tracking-wider text-xs font-semibold">Challenges</p>
              </div>
            </div>
            
            {/* Total Prizes */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-purple-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-50"></div>
              <div className="relative bg-black/40 backdrop-blur-sm border border-green-500/30 rounded-xl p-6 text-center hover:border-green-500/50 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-green-400" />
                </div>
                <div className="text-3xl font-black text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <span className="text-green-400">${Math.round(totalPrizePool + upcomingPrizePool).toLocaleString()}</span>
                </div>
                {/* Show bonus prizes breakdown */}
                {(activeNonMoney.gameCodes + scheduledNonMoney.gameCodes > 0 || activeNonMoney.items + scheduledNonMoney.items > 0) && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-2 text-xs">
                    {(activeNonMoney.gameCodes + scheduledNonMoney.gameCodes) > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 rounded-full text-purple-400">
                        <Key className="w-3 h-3" />
                        {activeNonMoney.gameCodes + scheduledNonMoney.gameCodes} Code{(activeNonMoney.gameCodes + scheduledNonMoney.gameCodes) > 1 ? 's' : ''}
                      </span>
                    )}
                    {(activeNonMoney.items + scheduledNonMoney.items) > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 rounded-full text-orange-400">
                        <Gift className="w-3 h-3" />
                        {activeNonMoney.items + scheduledNonMoney.items} Item{(activeNonMoney.items + scheduledNonMoney.items) > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-green-400 uppercase tracking-wider text-xs font-semibold mt-2">Total Prizes</p>
              </div>
            </div>
            
            {/* Registered Users */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-transparent rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-50"></div>
              <div className="relative bg-black/40 backdrop-blur-sm border border-blue-500/30 rounded-xl p-6 text-center hover:border-blue-500/50 transition-all duration-300">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                  </svg>
                </div>
                <div className="text-4xl font-black text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {totalUsers.toLocaleString()}
                </div>
                <p className="text-blue-400 uppercase tracking-wider text-xs font-semibold">Users</p>
              </div>
            </div>
            
            {/* Status */}
            <div className="relative group">
              <div className={`absolute inset-0 bg-gradient-to-br rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-50 ${
                activeChallenges.length > 0 ? 'from-green-500/20 to-transparent' : scheduledChallenges.length > 0 ? 'from-yellow-500/20 to-transparent' : 'from-gray-500/20 to-transparent'
              }`}></div>
              <div className={`relative bg-black/40 backdrop-blur-sm rounded-xl p-6 text-center transition-all duration-300 border ${
                activeChallenges.length > 0 ? 'border-green-500/30 hover:border-green-500/50' : scheduledChallenges.length > 0 ? 'border-yellow-500/30 hover:border-yellow-500/50' : 'border-gray-500/30 hover:border-gray-500/50'
              }`}>
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  activeChallenges.length > 0 ? 'bg-green-500/20' : scheduledChallenges.length > 0 ? 'bg-yellow-500/20' : 'bg-gray-500/20'
                }`}>
                  <span className={`w-4 h-4 rounded-full ${
                    activeChallenges.length > 0 ? 'bg-green-400 animate-pulse' : scheduledChallenges.length > 0 ? 'bg-yellow-400' : 'bg-gray-400'
                  }`}></span>
                </div>
                <div className={`text-3xl font-black mb-1 ${
                  activeChallenges.length > 0 ? 'text-green-400' : scheduledChallenges.length > 0 ? 'text-yellow-400' : 'text-gray-400'
                }`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {activeChallenges.length > 0 ? 'LIVE' : scheduledChallenges.length > 0 ? 'SOON' : 'ENDED'}
                </div>
                <p className={`uppercase tracking-wider text-xs font-semibold ${
                  activeChallenges.length > 0 ? 'text-green-400' : scheduledChallenges.length > 0 ? 'text-yellow-400' : 'text-gray-400'
                }`}>Status</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-red-900/20">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <p className="flex items-center justify-center gap-2 text-gray-600">
            <Trophy className="w-4 h-4 text-red-500" />
            CesarM Challenges
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm">
            <a href="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors">
              Privacy Policy
            </a>
            <span className="text-gray-700">•</span>
            <a href="/terms" className="text-gray-500 hover:text-gray-300 transition-colors">
              Terms of Service
            </a>
            <span className="text-gray-700">•</span>
            <a 
              href="https://discord.gg/TsqcHNm647" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Discord
            </a>
            <span className="text-gray-700">•</span>
            <a 
              href="https://x.com/CesarMChallenge" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Twitter/X
            </a>
          </div>
          <p className="text-xs text-gray-700">
            © 2026 CesarM Challenges. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
